import type { EndItemReferences, NsnReport } from '@/api/generatedApi';
import { ArrowForward } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

const Chain = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '8px',
  margin: '4px 0 12px',
});

const ChainStep = styled(Box, { shouldForwardProp: property => property !== 'current' })<{ current?: boolean }>(
  ({ theme, current }) => ({
    border: `1px solid ${current ? theme.palette.primary.main : theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: '4px 10px',
    maxWidth: '320px',
  })
);

const ChainArrow = () => <ArrowForward fontSize='small' color='disabled' />;

const ChainBox = ({
  caption,
  value,
  current,
  testId,
}: {
  caption: string;
  value: string;
  current?: boolean;
  testId?: string;
}) => (
  <ChainStep current={current}>
    <Typography variant='caption' color='text.secondary' component='div'>
      {caption}
    </Typography>
    <Typography variant='body2' sx={{ fontWeight: current ? 600 : 400 }} data-testid={testId}>
      {value}
    </Typography>
  </ChainStep>
);

export const describeReferencedBy = (references?: EndItemReferences): string => {
  if (!references) return 'Not checked for this report';
  if (references.status === 'unavailable') return 'Unavailable (the catalog search timed out)';
  const count = references.matches.length;
  if (count === 0) return references.truncated ? 'None confirmed before the search hit its cap' : 'None found';
  const noun = count === 1 ? 'part' : 'parts';
  return references.truncated ? `First ${count} ${noun} shown; more exist` : `${count} ${noun}`;
};

export const EndItemChain = ({ nsnReport }: { nsnReport: NsnReport }) => (
  <Chain data-testid='end-items-chain'>
    <ChainBox
      caption='Parts that go into this'
      value={describeReferencedBy(nsnReport.endItemReferences)}
      testId='end-items-referenced-by'
    />
    <ChainArrow />
    <ChainBox caption='This part' value={nsnReport.nsn} current />
    <ChainArrow />
    <ChainBox caption='Goes into (end item)' value={nsnReport.endItemDescription || 'Not listed in FLIS'} />
  </Chain>
);
